import { useState, useCallback, useEffect } from 'react';

/**
 * useAccessibilityPrefs — CSS-first accessibility preferences.
 *
 * Reads/writes a small preferences object from localStorage and mirrors each
 * preference as a `data-a11y-*` attribute on `<html>`.  All visual effects are
 * handled by CSS attribute-selector rules in index.css, so changing a
 * preference never triggers React re-renders in other components.
 *
 * Preferences stored:
 *   reducedMotion : boolean  – disable animations / transitions
 *   dyslexiaFont  : boolean  – swap to Atkinson Hyperlegible
 *   textSpacing   : 'default' | 'comfortable' | 'spacious'
 *   focusMode     : boolean  – hide non-essential form chrome
 *   focusRing     : boolean  – thicker, higher-contrast focus rings
 *
 * The hook is designed to be called from PatientSettings (to render toggle UI)
 * and nowhere else needs to import it — CSS does the rest.
 */

const STORAGE_KEY = 'td-a11y-prefs';

const FONT_LINK_ID = 'a11y-atkinson-font';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400;1,700&display=swap';

// Detect the OS-level reduced-motion preference (used as the default value).
function osReducedMotion() {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function defaultPrefs() {
  return {
    reducedMotion: osReducedMotion(),
    dyslexiaFont: false,
    textSpacing: 'default',
    focusMode: false,
    focusRing: false,
  };
}

// Read persisted prefs (or fall back to defaults).
function readPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPrefs();
    return { ...defaultPrefs(), ...JSON.parse(raw) };
  } catch {
    return defaultPrefs();
  }
}

// Persist prefs to localStorage.
function writePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage unavailable (private browsing, quota) — silent fail.
  }
}

// Lazy-load Atkinson Hyperlegible from Google Fonts when enabled, remove when
// disabled.  Only one <link> is ever injected, keyed by a fixed element id.
function syncFontLink(enabled) {
  const existing = document.getElementById(FONT_LINK_ID);
  if (enabled && !existing) {
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  } else if (!enabled && existing) {
    existing.remove();
  }
}

// Apply every preference as a data attribute on <html>.
function applyToDOM(prefs) {
  const root = document.documentElement;
  root.setAttribute('data-a11y-motion', prefs.reducedMotion ? 'reduce' : 'no-preference');
  root.setAttribute('data-a11y-font', prefs.dyslexiaFont ? 'dyslexic' : 'default');
  root.setAttribute('data-a11y-spacing', prefs.textSpacing);
  root.setAttribute('data-a11y-focus-mode', String(prefs.focusMode));
  root.setAttribute('data-a11y-focus-ring', prefs.focusRing ? 'enhanced' : 'default');
  syncFontLink(prefs.dyslexiaFont);
}

// On first import, immediately apply any previously-saved preferences so the
// page renders correctly before React even mounts.  This is an intentional
// module-level side-effect — it prevents a flash of un-styled content on reload.
applyToDOM(readPrefs());

export default function useAccessibilityPrefs() {
  const [prefs, setPrefs] = useState(readPrefs);

  // Keep DOM in sync if prefs state changes (e.g. from another hook instance).
  useEffect(() => {
    applyToDOM(prefs);
  }, [prefs]);

  const updatePref = useCallback((key, value) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      writePrefs(next);
      applyToDOM(next);
      return next;
    });
  }, []);

  // Reset all preferences back to defaults.
  const resetPrefs = useCallback(() => {
    const defaults = defaultPrefs();
    writePrefs(defaults);
    applyToDOM(defaults);
    setPrefs(defaults);
  }, []);

  return { prefs, updatePref, resetPrefs };
}
